import React, { useEffect, useMemo, useState } from "react";

import { enUS, fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button } from "@/src/components/ui/button";
import { Calendar } from "@/src/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/src/components/ui/popover";
import { cn } from "@/src/lib/utils";

import type { Locale } from "date-fns";
import type { Moment } from "moment";
import type { JSX } from "react";
import type { DateRange, Matcher } from "react-day-picker";

import type { AppState } from "@/src/reducers";

interface DateRangePickerProps {
  startDate?: Moment | null;
  endDate?: Moment | null;
  minDate?: Moment | null;
  onChange: (startDate: Moment, endDate: Moment) => void;
}

interface DatePreset {
  text: string;
  start: Moment;
  end: Moment;
}

interface DatePresetDefinition {
  key: string;
  getStart: () => Moment;
  getEnd?: () => Moment;
}

// Plancher du sélecteur d'année sans minDate : 10 ans couvrent l'historique utile.
const DEFAULT_HISTORY_YEARS = 10;

const DATE_FNS_LOCALES: Record<string, Locale> = { fr, en: enUS };

function toDate(value?: Moment | null): Date | undefined {
  return value ? value.toDate() : undefined;
}

function toRange(
  startDate?: Moment | null,
  endDate?: Moment | null,
): DateRange | undefined {
  return startDate
    ? { from: startDate.toDate(), to: toDate(endDate) }
    : undefined;
}

function isSameDay(a?: Moment | null, b?: Moment | null): boolean {
  return !!a && !!b && a.isSame(b, "day");
}

function PresetButton({
  isActive,
  onSelect,
  preset,
}: {
  isActive: boolean;
  onSelect: (preset: DatePreset) => void;
  preset: DatePreset;
}): JSX.Element {
  const handleClick = (): void => {
    onSelect(preset);
  };

  return (
    <Button
      className="justify-start font-normal"
      onClick={handleClick}
      size="sm"
      variant={isActive ? "secondary" : "ghost"}
    >
      {preset.text}
    </Button>
  );
}

function DateRangePicker({
  startDate,
  endDate,
  minDate,
  onChange,
}: DateRangePickerProps): JSX.Element {
  const { t } = useTranslation("editor-utils");
  const lang = useSelector((state: AppState) => state.variableReducer.lang);
  const [open, setOpen] = useState(false);

  const locale = DATE_FNS_LOCALES[lang] ?? enUS;

  const disabledDays = useMemo<Matcher[]>(() => {
    const matchers: Matcher[] = [{ after: new Date() }];
    if (minDate) matchers.push({ before: minDate.toDate() });
    return matchers;
  }, [minDate]);

  const presets = useMemo<DatePreset[]>(() => {
    const clampStart = (start: Moment): Moment =>
      minDate && start.isBefore(minDate) ? minDate.clone() : start;

    const definitions: DatePresetDefinition[] = [
      { key: "today", getStart: () => moment().startOf("day") },
      { key: "yesterday", getStart: () => moment().subtract(1, "day") },
      { key: "week", getStart: () => moment().startOf("week") },
      { key: "month", getStart: () => moment().startOf("month") },
      {
        key: "3_months",
        getStart: () => moment().startOf("month").subtract(3, "months"),
      },
      {
        key: "6_months",
        getStart: () => moment().startOf("month").subtract(6, "months"),
      },
      { key: "year", getStart: () => moment().startOf("year") },
      {
        key: "last_year",
        getStart: () => moment().subtract(1, "year").startOf("year"),
        getEnd: () => moment().subtract(1, "year").endOf("year"),
      },
      { key: "all_time", getStart: () => moment("01-01-1970", "DD-MM-YYYY") },
    ];

    return definitions
      .map(({ key, getStart, getEnd = () => moment() }) => ({
        text: t(key),
        start: clampStart(getStart()),
        end: getEnd(),
      }))
      .filter((preset) => !preset.start.isAfter(preset.end));
  }, [t, minDate]);

  const startMonth = useMemo(
    () =>
      minDate
        ? minDate.clone().startOf("month").toDate()
        : moment()
            .subtract(DEFAULT_HISTORY_YEARS, "years")
            .startOf("year")
            .toDate(),
    [minDate],
  );
  const endMonth = useMemo(() => moment().startOf("month").toDate(), []);

  // Plage en cours affichée par le calendrier : bufferisée pour refléter le 1er
  // clic, resynchronisée quand le parent change les dates (preset, reset…).
  const [range, setRange] = useState<DateRange | undefined>(() =>
    toRange(startDate, endDate),
  );

  useEffect(() => {
    setRange(toRange(startDate, endDate));
  }, [startDate, endDate]);

  const label = useMemo(() => {
    const start = startDate
      ? startDate.clone().locale(lang).format("ll")
      : null;
    const end = endDate ? endDate.clone().locale(lang).format("ll") : null;

    if (start && end) return `${start} → ${end}`;
    if (start) return `${start} → ${t("end_date")}`;
    return `${t("start_date")} → ${t("end_date")}`;
  }, [startDate, endDate, lang, t]);

  const hasRange = startDate != null || endDate != null;

  const handlePreset = (preset: DatePreset): void => {
    onChange(preset.start, preset.end);
    setOpen(false);
  };

  const handleSelect = (nextRange: DateRange | undefined): void => {
    setRange(nextRange);

    if (nextRange?.from && nextRange.to) {
      onChange(moment(nextRange.from), moment(nextRange.to));
    }
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "border-input bg-card aria-expanded:bg-card h-9 justify-start rounded-md px-3 py-1 text-base font-normal shadow-xs md:text-sm",
            !hasRange && "text-muted-foreground",
          )}
        >
          <CalendarIcon className="mr-2 size-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-auto p-0">
        <div className="flex flex-col border-r p-2">
          {presets.map((preset) => (
            <PresetButton
              key={preset.text}
              onSelect={handlePreset}
              preset={preset}
              isActive={
                isSameDay(preset.start, startDate) &&
                isSameDay(preset.end, endDate)
              }
            />
          ))}
        </div>
        <Calendar
          captionLayout="dropdown"
          defaultMonth={toDate(endDate) ?? toDate(startDate)}
          disabled={disabledDays}
          endMonth={endMonth}
          locale={locale}
          mode="range"
          numberOfMonths={2}
          onSelect={handleSelect}
          selected={range}
          startMonth={startMonth}
        />
      </PopoverContent>
    </Popover>
  );
}

export default DateRangePicker;
